import * as React from 'react'

type Props = {
  posX: number
  posY: number
}

export type HotspotPosition = {
  top?: string
  left?: string
  right?: string
  bottom?: string
  transform?: string
}

type Output = [
  React.RefObject<HTMLDivElement | null> | null,
  React.RefObject<HTMLDivElement | null> | null,
  HotspotPosition
]

const tooltipPositions = [
  'topLeft',
  'topCenter',
  'topRight',
  'centerLeft',
  'centerCenter',
  'centerRight',
  'bottomLeft',
  'bottomCenter',
  'bottomRight'
] as const

type TooltipPosition = (typeof tooltipPositions)[number]

const getTooltipPosition = (posX: number, posY: number): TooltipPosition => {
  if (posX < 333 && posY < 333) return 'topLeft'
  if (posX > 333 && posX < 666 && posY < 333) return 'topCenter'
  if (posX > 666 && posY < 333) return 'topRight'
  if (posX < 333 && posY > 333 && posY < 666) return 'centerLeft'
  if (posX > 333 && posX < 666 && posY > 333 && posY < 666) {
    // show box on top or bottom to prevent box to move out of viewable area
    if (posY < 510) return 'topCenter'
    else return 'bottomCenter'
  }
  if (posX > 666 && posY > 333 && posY < 666) return 'centerRight'
  if (posX < 333 && posY > 666) return 'bottomLeft'
  if (posX > 333 && posX < 666 && posY > 666) return 'bottomCenter'
  return 'bottomRight'
}

const getTooltipStyle = (
  tooltipPosition: TooltipPosition,
  offsetWidth: number
) => {
  const styles = {
    topLeft: {
      top: '0%',
      left: '110%'
      // transform: `translateY(-${offsetWidth}px)`
    },
    topCenter: { top: '110%', left: '50%', transform: 'translateX(-50%)' },
    topRight: {
      top: '100%',
      right: '110%',
      transform: `translateY(-${offsetWidth}px)`
    },
    centerLeft: { left: '110%', top: '50%', transform: 'translateY(-50%)' },
    // centerCenter: { left: '110%', top: '50%', transform: 'translateY(-50%)' },
    centerRight: { right: '110%', top: '50%', transform: 'translateY(-50%)' },
    bottomLeft: {
      bottom: '100%',
      left: '110%',
      transform: `translateY(${offsetWidth}px)`
    },
    bottomCenter: {
      bottom: '110%',
      left: '50%',
      transform: 'translateX(-50%)'
    },
    bottomRight: {
      bottom: '100%',
      right: '110%',
      transform: `translateY(${offsetWidth}px)`
    }
  }
  return styles[tooltipPosition]
}

export default function useContentPosition({ posX, posY }: Props): Output {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const hotspotRef = React.useRef<HTMLDivElement>(null)

  const [tooltipPosition, setTooltipPosition] =
    React.useState<TooltipPosition>('topLeft')

  React.useEffect(() => {
    setTooltipPosition(getTooltipPosition(posX, posY))
  }, [posX, posY])

  const tooltipPositionStyle = getTooltipStyle(
    tooltipPosition,
    hotspotRef.current?.offsetHeight || 0
  )

  return [contentRef, hotspotRef, tooltipPositionStyle]
}
